import React, { useEffect, useMemo, useRef, useState } from 'react'
import './RelatedPromoPicker.sass'
import Icon from '../../../ui/Icon/Icon'
import DropdownBox from '../../../ui/DropdownBox/DropdownBox'
import buildRelatedPromoSelection from '../../../api/marketing/buildRelatedPromoSelection'
import { PROMO_SCHEDULE_STATUS } from '../../../api/marketing/promoScheduleConstants'

const MAX_RESULTS = 30

const filterPromoOptions = (options, query) => {
  const trimmedQuery = query.trim().toLowerCase()

  if (!trimmedQuery) {
    return options.slice(0, MAX_RESULTS)
  }

  return options
    .filter((option) => {
      const haystack = [option.label, option.dateRangeLabel, option.statusLabel]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      return haystack.includes(trimmedQuery)
    })
    .slice(0, MAX_RESULTS)
}

function RelatedPromoPicker({ value = null, onChange, options = [], isLoading, loadError, onLoadRequest }) {
  const [searchQuery, setSearchQuery] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [activeOptionIndex, setActiveOptionIndex] = useState(-1)
  const fieldRef = useRef(null)
  const dropdownRef = useRef(null)
  const inputRef = useRef(null)
  const optionRefs = useRef([])

  const isSearching = isFocused && !value
  const showDropdown = isSearching
  const hasFloatingLabel = Boolean(value) || searchQuery.length > 0
  const inputValue = value && !isSearching ? value.label : searchQuery

  const searchResults = useMemo(() => filterPromoOptions(options, searchQuery), [options, searchQuery])

  useEffect(() => {
    setActiveOptionIndex(searchResults.length > 0 ? 0 : -1)
    optionRefs.current = []
  }, [searchResults])

  useEffect(() => {
    const activeOption = optionRefs.current[activeOptionIndex]

    if (activeOption) {
      activeOption.scrollIntoView({ block: 'nearest' })
    }
  }, [activeOptionIndex])

  const handleSelectPromo = (option) => {
    onChange(buildRelatedPromoSelection(option))
    setSearchQuery('')
    setActiveOptionIndex(-1)
    setIsFocused(false)
    inputRef.current?.blur()
  }

  const handleOptionMouseDown = (event) => {
    event.preventDefault()
  }

  const handleFieldBlur = () => {
    window.setTimeout(() => {
      if (!fieldRef.current?.contains(document.activeElement) && !dropdownRef.current?.contains(document.activeElement)) {
        setIsFocused(false)
        setSearchQuery('')
        setActiveOptionIndex(-1)
      }
    }, 0)
  }

  const handleInputKeyDown = (event) => {
    if (!showDropdown || searchResults.length === 0) {
      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveOptionIndex((index) => (index < searchResults.length - 1 ? index + 1 : 0))
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveOptionIndex((index) => (index > 0 ? index - 1 : searchResults.length - 1))
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      const option = searchResults[activeOptionIndex]

      if (option) {
        handleSelectPromo(option)
      }
    }
  }

  const handleClear = (event) => {
    event.stopPropagation()
    onChange(null)
    setSearchQuery('')
    inputRef.current?.focus()
  }

  const handleFocus = () => {
    onLoadRequest?.()

    if (value) {
      onChange(null)
      setSearchQuery('')
    }

    setIsFocused(true)
  }

  const isActiveStatus = (status) => status === PROMO_SCHEDULE_STATUS.active

  return (
    <div className="UtmBuilder-RelatedPromoField">
      <div
        className={[
          'Field',
          'UtmBuilder-RelatedPromoControl',
          hasFloatingLabel ? 'Field_with_value' : '',
          value ? 'UtmBuilder-RelatedPromoControl_has_clear' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        ref={fieldRef}
        onClick={() => inputRef.current?.focus()}
      >
        <input
          ref={inputRef}
          type="text"
          className="UtmBuilder-RelatedPromoInput"
          value={inputValue}
          readOnly={Boolean(value) && !isSearching}
          onChange={(event) => setSearchQuery(event.target.value)}
          onFocus={handleFocus}
          onBlur={handleFieldBlur}
          onKeyDown={handleInputKeyDown}
          role="combobox"
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          aria-activedescendant={
            showDropdown && activeOptionIndex >= 0
              ? `related-promo-option-${searchResults[activeOptionIndex]?.id}`
              : undefined
          }
        />

        {value && !isLoading ? (
          <button
            type="button"
            className="UtmBuilder-RelatedPromoClear"
            title="Очистити"
            onMouseDown={handleOptionMouseDown}
            onClick={handleClear}
          >
            <Icon name="times" weight="regular" size={12} />
          </button>
        ) : null}

        {isLoading && <span className="UtmBuilder-RelatedPromoLoading" aria-hidden="true" />}
        <span className="Field-Bar" />
        <label>Повʼязана промо / акція</label>
      </div>

      <div className="UtmBuilder-RelatedPromoSelectedMetaSlot">
        {value?.dateRangeLabel || value?.statusLabel ? (
          <p className="UtmBuilder-RelatedPromoSelectedMeta">
            {value.dateRangeLabel ? <span>{value.dateRangeLabel}</span> : <span />}
            {value.statusLabel ? (
              <span
                className={[
                  'UtmBuilder-RelatedPromoSelectedMetaStatus',
                  isActiveStatus(value.status) ? 'UtmBuilder-RelatedPromoSelectedMetaStatus_active' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {value.statusLabel}
              </span>
            ) : null}
          </p>
        ) : null}
      </div>

      {loadError ? (
        <p className="UtmBuilder-RelatedPromoHint UtmBuilder-RelatedPromoHint_error">{loadError}</p>
      ) : null}

      {showDropdown && (
        <DropdownBox ref={dropdownRef} styles={{ top: '43px' }}>
          {isLoading ? (
            <p className="EmptyMessage">Завантаження...</p>
          ) : searchResults.length === 0 ? (
            <p className="EmptyMessage">Промо / акцій не знайдено</p>
          ) : (
            searchResults.map((option, index) => (
              <li
                key={option.id}
                ref={(element) => {
                  optionRefs.current[index] = element
                }}
                id={`related-promo-option-${option.id}`}
                role="option"
                aria-selected={index === activeOptionIndex}
                className={[
                  'DropdownBox-Element',
                  index === activeOptionIndex ? 'DropdownBox-Element_theme_active' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onMouseDown={handleOptionMouseDown}
                onMouseEnter={() => setActiveOptionIndex(index)}
                onClick={() => handleSelectPromo(option)}
              >
                <div className="UtmBuilder-RelatedPromoOption">
                  <div className="UtmBuilder-RelatedPromoOptionTitle">{option.label}</div>
                  <div className="UtmBuilder-RelatedPromoOptionMeta">
                    <span className="UtmBuilder-RelatedPromoOptionMetaLeft">{option.dateRangeLabel || '—'}</span>
                    <span
                      className={[
                        'UtmBuilder-RelatedPromoOptionMetaRight',
                        isActiveStatus(option.status) ? 'UtmBuilder-RelatedPromoOptionStatus_active' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {option.statusLabel}
                    </span>
                  </div>
                </div>
              </li>
            ))
          )}
        </DropdownBox>
      )}
    </div>
  )
}

export default RelatedPromoPicker
